/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import { useInsuranceCarriers, useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { genderOptions } from '@brands/Utils/selectButtonOptions';
import { insuranceThroughOptionsForInsurancePage, stateOptions } from '@brands/Utils/selectOptions';
import { truncateFilename } from '@brands/Utils/truncateString';
import { Checkbox } from '@mui/material';
import dayjs from 'dayjs';
import { E164Number } from 'libphonenumber-js';
import React, { useEffect, useMemo, useState } from 'react';
import { FieldErrors, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { MdOutlineUploadFile } from 'react-icons/md';

import TextField from '../../Components/Inputs/MuiTextField/TextField';
import MuiToggle from '../../Components/Inputs/MuiToggle/MuiToggle';
import styles from './styles.module.scss';
import { InsuranceForm, InsuranceImages } from './utils/types';

type InsuranceCarrierOptions = {
  value: number;
  label: string;
};

/** Sentinel: not a real carrier id; means “no insurance” in optional-carrier flows (e.g. Collector add user). */
const NO_INSURANCE_CARRIER_OPTION_VALUE = 0;

type InsuranceFormSectionProps = {
  isSecondary: boolean;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  hideTitle?: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDrop: (event: React.DragEvent<HTMLDivElement>, type: keyof InsuranceImages) => void;
  handleDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  deleteMedia: (mediaName: string, type: keyof InsuranceImages) => void;
  phoneInputRef: React.RefObject<HTMLInputElement>;
  isDragging: boolean;
  images: InsuranceImages;
  isFromPatientIntake?: boolean;
  otherInsuranceId?: number | undefined;
  addOtherOptionToInsuranceCompany?: boolean;
  profileAddressSource?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  /** When true, insurance company is optional and the list includes “No insurance”. */
  allowNoInsuranceCarrier?: boolean;
};

const InsuranceFormSection = ({
  isSecondary,
  register,
  setValue,
  getValues,
  watch,
  errors,
  hideTitle = false,
  handleChange,
  handleDrop,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  deleteMedia,
  phoneInputRef,
  isDragging,
  images,
  isFromPatientIntake = false,
  otherInsuranceId = undefined,
  profileAddressSource,
  allowNoInsuranceCarrier = false,
}: InsuranceFormSectionProps): JSX.Element => {
  const screenSize = useWindowSize();
  const insuranceCarriers = useInsuranceCarriers();
  const [showInsuranceInputs, setShowInsuranceInputs] = useState(false);
  const insuranceCarrierOptions: InsuranceCarrierOptions[] = useMemo(() => {
    const baseOptions = insuranceCarriers.map((insuranceCarrier) => ({
      value: insuranceCarrier.id,
      label: insuranceCarrier.name,
    }));

    if (isFromPatientIntake && otherInsuranceId) {
      baseOptions.push({ value: otherInsuranceId, label: 'Other' });
    }

    if (allowNoInsuranceCarrier) {
      return [{ value: NO_INSURANCE_CARRIER_OPTION_VALUE, label: 'No insurance' }, ...baseOptions];
    }
    return baseOptions;
  }, [insuranceCarriers, allowNoInsuranceCarrier, isFromPatientIntake, otherInsuranceId]);

  const { userInfo } = useAppSelector(selectAuth);
  const addressSource = profileAddressSource ?? (userInfo.address || {});

  const [sameAddress, setSameAddress] = useState(false);

  const selectSameAddress = (isSelect = true): void => {
    const addressInputs = [
      ['insurance_address1', 'address1'],
      ['insurance_address2', 'address2'],
      ['insurance_city1', 'city'],
      ['insurance_state1', 'state'],
      ['insurance_zip_code1', 'zip_code'],
    ];
    setSameAddress(isSelect);
    if (isSelect) {
      addressInputs.forEach((input) => {
        setValue(
          `${isSecondary ? 'secondary_' : ''}${input[0]}` as keyof InsuranceForm,
          (addressSource as any)?.[input[1]] ?? '',
          {
            shouldValidate: true,
          },
        );
      });
    } else {
      addressInputs.forEach((input) => {
        setValue(
          `${isSecondary ? 'secondary_' : ''}${input[0]}` as keyof InsuranceForm,
          input[0] === 'insurance_state1' ? '' : undefined,
          {
            shouldValidate: true,
          },
        );
      });
    }
  };

  const fieldName = (name: keyof InsuranceForm): keyof InsuranceForm =>
    (isSecondary ? `secondary_${name}` : name) as keyof InsuranceForm;

  useEffect(() => {
    const addressInputs = [
      ['insurance_address1', 'address1'],
      ['insurance_address2', 'address2'],
      ['insurance_city1', 'city'],
      ['insurance_state1', 'state'],
      ['insurance_zip_code1', 'zip_code'],
    ];
    const isSameAddress = addressInputs.every(
      (input) =>
        getValues(fieldName(input[0] as keyof InsuranceForm)) === ((addressSource as any)?.[input[1]] ?? ''),
    );
    setSameAddress(isSameAddress);
  }, [getValues(), addressSource]);

  const rawDob = getValues(fieldName('insurance_date_of_birth'));
  const dobValue = rawDob ? dayjs(rawDob as string) : null;

  const carrierId = watch(fieldName('insurance_carrier_id'));

  useEffect(() => {
    setShowInsuranceInputs(carrierId != null && Number(carrierId) > 0);
  }, [carrierId]);

  return (
    <>
      {!hideTitle &&
        (!isFromPatientIntake ? (
          <div className={styles.insuranceHeader}>
            {!isSecondary ? <h2>Primary insurance information</h2> : <h2>Secondary insurance information</h2>}
          </div>
        ) : isSecondary ? (
          <span className={styles.formContentTitle}>Secondary insurance information</span>
        ) : (
          ''
        ))}
      <MUISelectInput
        {...register(fieldName('insurance_carrier_id'))}
        label="Insurance company"
        data-testid="insurance-carrier-id"
        errors={errors[fieldName('insurance_carrier_id')] ? errors : {}}
        value={(() => {
          const v = getValues(fieldName('insurance_carrier_id'));
          return v === null || v === undefined ? '' : v;
        })()}
        name={fieldName('insurance_carrier_id')}
        onChange={(value: string | number) => {
          if (value === '' || value === null || value === undefined) {
            setValue(fieldName('insurance_carrier_id'), null, { shouldValidate: true });
            return;
          }
          const n = typeof value === 'string' ? Number(value) : value;
          if (n === NO_INSURANCE_CARRIER_OPTION_VALUE) {
            setValue(fieldName('insurance_carrier_id'), NO_INSURANCE_CARRIER_OPTION_VALUE, {
              shouldValidate: true,
            });
            return;
          }
          if (!Number.isNaN(n) && n > 0) {
            setValue(fieldName('insurance_carrier_id'), n, { shouldValidate: true });
          }
        }}
        required={!allowNoInsuranceCarrier}
        inputClass={`${styles.profileInputClass} fs-exclude`}
        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
        options={insuranceCarrierOptions}
      />
      {showInsuranceInputs && (
        <>
          {' '}
          {isFromPatientIntake && Number(getValues('insurance_carrier_id')) === otherInsuranceId && (
            <TextField
              {...register('other_insurance_name')}
              errors={errors}
              label="Name of insurance company (optional)"
              data-testid="insurance-company-other"
              name="other_insurance_name"
              value={getValues(fieldName('other_insurance_name'))}
              onChange={(e) => {
                setValue(fieldName('other_insurance_name'), e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
            />
          )}
          {!(isFromPatientIntake && Number(getValues('insurance_carrier_id')) === otherInsuranceId) && (
            <>
              <div className={styles.insuranceMultipleFields}>
                <TextField
                  {...register(fieldName('member_id'))}
                  label="Member ID"
                  data-testid="insurance-member-id"
                  errors={errors[fieldName('member_id')] ? errors : {}}
                  name={fieldName('member_id')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(fieldName('member_id'), e.target.value, { shouldValidate: true });
                  }}
                  value={getValues(fieldName('member_id'))}
                  required
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                />
                <TextField
                  {...register(fieldName('group_number'))}
                  label="Group number (optional)"
                  data-testid="insurance-group-number"
                  errors={errors[fieldName('group_number')] ? errors : {}}
                  name={fieldName('group_number')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(fieldName('group_number'), e.target.value, { shouldValidate: true });
                  }}
                  value={getValues(fieldName('group_number'))}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                />
              </div>
              <PhoneInput
                {...register(fieldName('insurance_phone_number'))}
                value={watch(fieldName('insurance_phone_number')) as E164Number}
                id={fieldName('insurance_phone_number')}
                data-testid="insurance-phone-number"
                name={fieldName('insurance_phone_number')}
                onChange={(val: E164Number | string | undefined) => {
                  if (val === undefined || val === '') {
                    setValue(fieldName('insurance_phone_number'), '', { shouldValidate: true });
                    return;
                  }
                  setValue(fieldName('insurance_phone_number'), val, { shouldValidate: true });
                }}
                required
                label="Insurance phone number"
                errors={errors}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                ref={phoneInputRef}
              />
              <TextField
                {...register(fieldName('insurance_address'))}
                label="Insurance street address"
                data-testid="insurance-address"
                errors={errors[fieldName('insurance_address')] ? errors : {}}
                name={fieldName('insurance_address')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValue(fieldName('insurance_address'), e.target.value, { shouldValidate: true });
                }}
                value={getValues(fieldName('insurance_address'))}
                required
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
              />
              <div className={styles.insuranceMultipleFields}>
                <TextField
                  {...register(fieldName('insurance_city'))}
                  label="Insurance city"
                  data-testid="insurance-city"
                  errors={errors[fieldName('insurance_city')] ? errors : {}}
                  name={fieldName('insurance_city')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(fieldName('insurance_city'), e.target.value, { shouldValidate: true });
                  }}
                  value={getValues(fieldName('insurance_city'))}
                  required
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                />
                <MUISelectInput
                  {...register(fieldName('insurance_state'))}
                  label="Insurance state"
                  data-testid="insurance-state"
                  errors={errors[fieldName('insurance_state')] ? errors : {}}
                  value={getValues(fieldName('insurance_state')) || ''}
                  name={fieldName('insurance_state')}
                  onChange={(val: string) => {
                    setValue(fieldName('insurance_state'), val, { shouldValidate: true });
                  }}
                  required
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                  options={stateOptions}
                  wrapperStyle={{ height: '100%' }}
                />
                <TextField
                  {...register(fieldName('insurance_zip_code'))}
                  label="Insurance zip code"
                  data-testid="insurance-zipcode"
                  errors={errors[fieldName('insurance_zip_code')] ? errors : {}}
                  name={fieldName('insurance_zip_code')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(fieldName('insurance_zip_code'), e.target.value, { shouldValidate: true });
                  }}
                  value={getValues(fieldName('insurance_zip_code'))}
                  required
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                />
              </div>
              <MuiToggle
                label="Are you the policy holder for this insurance?"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                data-testid="insurance-the-policy-holder"
                selectedValue={
                  getValues(fieldName('insurance_through')) === 'myself'
                    ? 'yes'
                    : getValues(fieldName('insurance_through')) === '' ||
                        getValues(fieldName('insurance_through')) === undefined
                      ? ''
                      : 'no'
                }
                onChange={(val: string) => {
                  if (val === 'yes') {
                    setValue(fieldName('insurance_through'), 'myself', { shouldValidate: true });
                  }
                  if (val === 'no') {
                    setValue(fieldName('insurance_through'), 'someone_else', { shouldValidate: true });
                  }
                }}
              />
              {getValues(fieldName('insurance_through')) !== 'myself' &&
                getValues(fieldName('insurance_through')) &&
                getValues(fieldName('insurance_through')) !== '' && (
                  <>
                    <MUISelectInput
                      {...register(fieldName('insurance_through'))}
                      label="Who is the policy holder for this insurance?"
                      data-testid="insurance-through"
                      errors={errors[fieldName('insurance_through')] ? errors : {}}
                      value={getValues(fieldName('insurance_through'))}
                      name={fieldName('insurance_through')}
                      onChange={(value: string) => {
                        if (value) {
                          setValue(fieldName('insurance_through'), value, { shouldValidate: true });
                        }
                      }}
                      required
                      inputClass={`${styles.profileInputClass} fs-exclude`}
                      wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      options={insuranceThroughOptionsForInsurancePage}
                    />
                    <div className={styles.insuranceHeader}>
                      <h2>Policy holder information</h2>
                    </div>
                    <div className={styles.insuranceMultipleFields}>
                      <TextField
                        {...register(fieldName('insurance_first_name'))}
                        label="First name"
                        data-testid="insurance-first-name"
                        errors={errors[fieldName('insurance_first_name')] ? errors : {}}
                        name={fieldName('insurance_first_name')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_first_name'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_first_name'))}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      />
                      <TextField
                        {...register(fieldName('insurance_last_name'))}
                        label="Last name"
                        data-testid="insurance-last-name"
                        errors={errors[fieldName('insurance_last_name')] ? errors : {}}
                        name={fieldName('insurance_last_name')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_last_name'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_last_name'))}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      />
                    </div>
                    <div className={styles.insuranceMultipleFields}>
                      <DateTextField
                        {...register(fieldName('insurance_date_of_birth'))}
                        label="Date of birth"
                        data-testid="insurance-date-of-birth"
                        errors={errors[fieldName('insurance_date_of_birth')] ? errors : {}}
                        name={fieldName('insurance_date_of_birth')}
                        onChange={(newValue: any) => {
                          let formattedDate = '';
                          if (newValue) {
                            formattedDate = dayjs(newValue).format('MM/DD/YYYY');
                          }

                          setValue(fieldName('insurance_date_of_birth'), formattedDate, { shouldValidate: true });
                        }}
                        value={dobValue}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      />
                      <MUISelectInput
                        {...register(fieldName('insurance_gender'))}
                        label="Gender assigned at birth"
                        data-testid="insurance-gender"
                        errors={errors[fieldName('insurance_gender')] ? errors : {}}
                        value={getValues(fieldName('insurance_gender')) || ''}
                        name={fieldName('insurance_gender')}
                        onChange={(e: string) => {
                          setValue(fieldName('insurance_gender'), e, { shouldValidate: true });
                        }}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                        options={genderOptions}
                      />
                    </div>
                    <div className={styles.checkboxContainer}>
                      <Checkbox
                        defaultChecked
                        className={styles.checkboxInput}
                        checked={sameAddress}
                        onChange={(e) => selectSameAddress(e.target.checked)}
                        id="subscriberHasSameAddress"
                        data-testid="insurance-same-address-checkbox"
                      />
                      <label
                        className={styles.checkboxLabel}
                        htmlFor="subscriberHasSameAddress"
                        data-testid="insurance-same-address-label"
                      >
                        Subscriber has same address
                      </label>
                    </div>
                    <div className={styles.insuranceMultipleFields}>
                      <TextField
                        {...register(fieldName('insurance_address1'))}
                        label="Address"
                        data-testid="insurance-address1"
                        errors={errors[fieldName('insurance_address1')] ? errors : {}}
                        name={fieldName('insurance_address1')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_address1'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_address1'))}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputFirstField}`}
                      />
                      <TextField
                        {...register(fieldName('insurance_address2'))}
                        label="Apartment, suite, etc. (optional)"
                        data-testid="insurance-address2"
                        errors={errors[fieldName('insurance_address2')] ? errors : {}}
                        name={fieldName('insurance_address2')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_address2'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_address2'))}
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputSecondField}`}
                      />
                    </div>
                    <div className={styles.insuranceMultipleFields}>
                      <TextField
                        {...register(fieldName('insurance_city1'))}
                        label="City"
                        data-testid="insurance-city1"
                        errors={errors[fieldName('insurance_city1')] ? errors : {}}
                        name={fieldName('insurance_city1')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_city1'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_city1'))}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      />
                      <MUISelectInput
                        {...register(fieldName('insurance_state1'))}
                        label="State"
                        data-testid="insurance-state1"
                        errors={errors[fieldName('insurance_state1')] ? errors : {}}
                        value={getValues(fieldName('insurance_state1')) || ''}
                        name={fieldName('insurance_state1')}
                        onChange={(val: string) => {
                          setValue(fieldName('insurance_state1'), val, { shouldValidate: true });
                        }}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                        options={stateOptions}
                        wrapperStyle={{ height: '100%' }}
                      />
                      <TextField
                        {...register(fieldName('insurance_zip_code1'))}
                        label="Zip code"
                        data-testid="insurance-zipcode1"
                        errors={errors[fieldName('insurance_zip_code1')] ? errors : {}}
                        name={fieldName('insurance_zip_code1')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(fieldName('insurance_zip_code1'), e.target.value, { shouldValidate: true });
                        }}
                        value={getValues(fieldName('insurance_zip_code1'))}
                        required
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.insuranceInputField}`}
                      />
                    </div>
                  </>
                )}
              <div className={styles.insuranceFileUpload}>
                <div className={styles.insuranceFileUploadHeader}>Upload your insurance card (optional)</div>
                <div
                  id={`${fieldName('front_of_card')}-dropzone`}
                  className={`${styles.draggingArea} ${isDragging ? styles.dragging : ''} fs-unmask`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, fieldName('front_of_card') as keyof InsuranceImages)}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  data-testid="insurance-upload-card-front-div"
                >
                  {images[fieldName('front_of_card') as keyof InsuranceImages] &&
                    images[fieldName('front_of_card') as keyof InsuranceImages].length > 0 && (
                      <div className={styles.grid}>
                        {images[fieldName('front_of_card') as keyof InsuranceImages].map((file) => {
                          const isBase64 = file.dataURL.startsWith('data:');
                          if (isBase64) {
                            const isPDF = /^data:application\/pdf/i.test(file.dataURL);
                            const isDoc =
                              /^data:application\/vnd.openxmlformats-officedocument.wordprocessingml.document|^data:application\/msword/i.test(
                                file.dataURL,
                              );
                            const isPpt =
                              /^data:application\/vnd.openxmlformats-officedocument.presentationml.presentation/i.test(
                                file.dataURL,
                              ) || /^data:application\/vnd.ms-powerpoint/i.test(file.dataURL);

                            const isImage = /^data:image/i.test(file.dataURL);
                            return (
                              <div className={`fs-exclude ${styles.imageWrap}`} key={file.dataURL}>
                                <span
                                  className={styles.removeImage}
                                  onClick={() =>
                                    deleteMedia(file.name, fieldName('front_of_card') as keyof InsuranceImages)
                                  }
                                >
                                  <IoClose />
                                </span>
                                {isPDF || isDoc || isPpt ? (
                                  <p>{truncateFilename(file.name, 20)}</p>
                                ) : isImage ? (
                                  <img src={file.dataURL} alt={file.name} className="fs-exclude" />
                                ) : null}
                              </div>
                            );
                          }
                          return (
                            <div className={`fs-exclude ${styles.imageWrap}`} key={file.dataURL}>
                              <span
                                className={styles.removeImage}
                                onClick={() =>
                                  deleteMedia(file.name, fieldName('front_of_card') as keyof InsuranceImages)
                                }
                              >
                                <IoClose />
                              </span>
                              {file.dataURL && (
                                <img src={file.dataURL} alt={file.name} className={`fs-exclude ${styles.image}`} />
                              )}
                              {file.name && <p className={styles.imageName}>{truncateFilename(file.name, 20)}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  <input
                    {...register(fieldName('front_of_card'))}
                    name=""
                    type="file"
                    id={fieldName('front_of_card') as string}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    data-testid="insurance-upload-card-front-input"
                    hidden
                    accept=".pdf, .doc, .docx, .ppt, .pptx, .png, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .heic"
                  />
                  <div className={styles.uploadContainer}>
                    {screenSize.width > 768 ? (
                      <>
                        <label htmlFor={fieldName('front_of_card')} className={styles.uploadImageLabel}>
                          Select files to upload
                        </label>
                        <span>or drag and drop</span>
                      </>
                    ) : (
                      <span>Add Front Of Card</span>
                    )}
                  </div>
                  <div className={styles.uploadContainer}>
                    <label
                      htmlFor={fieldName('front_of_card')}
                      className={styles.uploadButton}
                      data-testid="insurance-upload-card-front-button"
                    >
                      <MdOutlineUploadFile className={styles.uploadIcon} />
                      <span className={styles.uploadLabel}>
                        {screenSize.width > 768 ? 'Add Front Of Card' : 'Upload or take a photo'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className={styles.insuranceFileUpload}>
                <div
                  id={`${fieldName('back_of_card')}-dropzone`}
                  className={`${styles.draggingArea} ${isDragging ? styles.dragging : ''} fs-unmask`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, fieldName('back_of_card') as keyof InsuranceImages)}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  data-testid="insurance-upload-card-back-div"
                >
                  {images[fieldName('back_of_card') as keyof InsuranceImages] &&
                    images[fieldName('back_of_card') as keyof InsuranceImages].length > 0 && (
                      <div className={styles.grid}>
                        {images[fieldName('back_of_card') as keyof InsuranceImages].map((file) => {
                          const isBase64 = file.dataURL.startsWith('data:');
                          if (isBase64) {
                            const isPDF = /^data:application\/pdf/i.test(file.dataURL);
                            const isDoc =
                              /^data:application\/vnd.openxmlformats-officedocument.wordprocessingml.document|^data:application\/msword/i.test(
                                file.dataURL,
                              );
                            const isPpt =
                              /^data:application\/vnd.openxmlformats-officedocument.presentationml.presentation/i.test(
                                file.dataURL,
                              ) || /^data:application\/vnd.ms-powerpoint/i.test(file.dataURL);

                            const isImage = /^data:image/i.test(file.dataURL);
                            return (
                              <div className={`fs-exclude ${styles.imageWrap}`} key={file.dataURL}>
                                <span
                                  className={styles.removeImage}
                                  onClick={() =>
                                    deleteMedia(file.name, fieldName('back_of_card') as keyof InsuranceImages)
                                  }
                                >
                                  <IoClose />
                                </span>
                                {isPDF || isDoc || isPpt ? (
                                  <p>{truncateFilename(file.name, 20)}</p>
                                ) : isImage ? (
                                  <img src={file.dataURL} alt={file.name} className="fs-exclude" />
                                ) : null}
                              </div>
                            );
                          }
                          return (
                            <div className={`fs-exclude ${styles.imageWrap}`} key={file.dataURL}>
                              <span
                                className={styles.removeImage}
                                onClick={() =>
                                  deleteMedia(file.name, fieldName('back_of_card') as keyof InsuranceImages)
                                }
                              >
                                <IoClose />
                              </span>
                              {file.dataURL && (
                                <img src={file.dataURL} alt={file.name} className={`fs-exclude ${styles.image}`} />
                              )}
                              {file.name && <p className={styles.imageName}>{truncateFilename(file.name, 20)}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  <input
                    {...register(fieldName('back_of_card'))}
                    name=""
                    type="file"
                    id={fieldName('back_of_card') as string}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    data-testid="insurance-upload-card-back-input"
                    hidden
                    accept=".pdf, .doc, .docx, .ppt, .pptx, .png, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .heic"
                  />
                  <div className={styles.uploadContainer}>
                    {screenSize.width > 768 ? (
                      <>
                        <label htmlFor={fieldName('back_of_card')} className={styles.uploadImageLabel}>
                          Select files to upload
                        </label>
                        <span>or drag and drop</span>
                      </>
                    ) : (
                      <span>Add Back Of Card</span>
                    )}
                  </div>
                  <div className={styles.uploadContainer}>
                    <label
                      htmlFor={fieldName('back_of_card')}
                      className={styles.uploadButton}
                      data-testid="insurance-upload-card-back-button"
                    >
                      <MdOutlineUploadFile className={styles.uploadIcon} />
                      <span className={styles.uploadLabel}>
                        {screenSize.width > 768 ? 'Add Back Of Card' : 'Upload or take a photo'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              {!isFromPatientIntake && !isSecondary && (
                <MuiToggle
                  label="Do you currently have insurance coverage through a state-funded program such as Medicaid?"
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'not_sure', label: 'Not Sure' },
                  ]}
                  selectedValue={getValues(fieldName('state_funded_coverage_status'))}
                  onChange={(value: string) =>
                    setValue(fieldName('state_funded_coverage_status'), value, { shouldValidate: true })
                  }
                  error={errors[fieldName('state_funded_coverage_status')] as any}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default InsuranceFormSection;
